import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, User, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { WizardStepProps, CreateBookFormData } from '../../types';
import { UseFormReturn } from 'react-hook-form';
import {
  SKIN_TONE_OPTIONS, HAIR_COLOR_OPTIONS, EYE_COLOR_OPTIONS, HAIR_TEXTURE_OPTIONS, type AppearanceField, canonicalizeCharacterTraits,
} from '@/lib/wizard/appearance';

interface MultiCharacterAppearanceStepProps extends WizardStepProps {
  form: UseFormReturn<CreateBookFormData>;
  characterCount: number;
  onAdjust?: (field: AppearanceField) => void;
}

// Swatch per canonical option. Typed by the option lists, so adding an option without a swatch fails to compile.
const SKIN_SWATCH: Record<(typeof SKIN_TONE_OPTIONS)[number], string> = {
  light: '#FDDCBE', medium: '#F1B27A', olive: '#E5A073', tan: '#D99A6C', brown: '#C68863', dark: '#8D5D43',
};
const HAIR_SWATCH: Record<(typeof HAIR_COLOR_OPTIONS)[number], string> = {
  blonde: '#FAF0BE', brown: '#8B4513', black: '#2C1810', red: '#B55239', auburn: '#922724', gray: '#A9A9A9', white: '#F2F2F2',
};
const EYE_SWATCH: Record<(typeof EYE_COLOR_OPTIONS)[number], string> = {
  blue: '#6495ED', green: '#2E8B57', brown: '#8B4513', hazel: '#8E7618', gray: '#708090', amber: '#C68E17', black: '#1C1C1C',
};
const TEXTURE_ICON: Record<(typeof HAIR_TEXTURE_OPTIONS)[number], string> = {
  straight: '━', wavy: '〜', curly: '⚘', coily: '✿',
};
const AFIELDS: AppearanceField[] = ['skinTone', 'hairColor', 'eyeColor', 'hairTexture'];
const label = (v: string) => v.charAt(0).toUpperCase() + v.slice(1);

const SKIN_TONES = SKIN_TONE_OPTIONS.map((value) => ({ value, color: SKIN_SWATCH[value], label: label(value) }));
const HAIR_COLORS = HAIR_COLOR_OPTIONS.map((value) => ({ value, color: HAIR_SWATCH[value], label: label(value) }));
const EYE_COLORS = EYE_COLOR_OPTIONS.map((value) => ({ value, color: EYE_SWATCH[value], label: label(value) }));
const HAIR_TEXTURES = HAIR_TEXTURE_OPTIONS.map((value) => ({ value, label: label(value), icon: TEXTURE_ICON[value] }));

export function MultiCharacterAppearanceStep({ 
  onNext, 
  onBack, 
  form,
  characterCount,
  onAdjust
}: MultiCharacterAppearanceStepProps) {
  const [activeTab, setActiveTab] = useState(0);
  const mainCharacterIndex = form.watch('mainCharacterIndex') || 0;
  const charactersData = form.watch('characters') || [];

  // Legacy saved values ('silver', 'kinky') map onto the canonical swatches.
  useEffect(() => {
    const fixed = charactersData.map((c) => canonicalizeCharacterTraits(c));
    if (fixed.some((c, i) => AFIELDS.some((f) => c[f] !== charactersData[i][f]))) {
      form.setValue('characters', fixed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAppearanceChange = (field: AppearanceField, value: string) => {
    const updatedCharacters = [...charactersData];
    if (!updatedCharacters[activeTab]) {
      updatedCharacters[activeTab] = charactersData[activeTab] || {};
    }
    updatedCharacters[activeTab][field] = value;
    form.setValue('characters', updatedCharacters);
    onAdjust?.(field);
  };

  const isStepValid = () => {
    return charactersData.length === characterCount &&
      charactersData.every(char => 
        char.skinTone && char.hairColor && char.eyeColor && char.hairTexture
      );
  };

  const currentCharacter = charactersData[activeTab] || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* AI detection notice */}
      {charactersData.some(char => char.skinTone || char.hairColor) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-purple-50 p-4 rounded-lg border border-purple-200"
        >
          <p className="text-sm text-purple-800 flex items-center">
            <Sparkles className="mr-2" size={16} />
            <strong>Pre-filled:</strong>&nbsp;We've detected appearance features from your photo. 
            Feel free to adjust any details below.
          </p>
        </motion.div>
      )}

      {/* Tab navigation */}
      <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
        {charactersData.map((char, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={`flex-1 py-2 px-4 rounded-md transition-all text-sm font-medium ${
              activeTab === index 
                ? 'bg-white shadow-sm text-primary' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <User className="w-4 h-4 inline mr-1" />
            {char.name || `Person ${index + 1}`}
            {index === mainCharacterIndex && (
              <Badge variant="secondary" className="ml-2 text-xs">Main</Badge>
            )}
          </button>
        ))}
      </div>

      {/* Appearance options */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="space-y-6"
        >
          {/* Skin Tone */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Skin Tone
            </h3>
            <div className="flex gap-3 flex-wrap">
              {SKIN_TONES.map((tone) => (
                <motion.button
                  key={tone.value}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleAppearanceChange('skinTone', tone.value)}
                  className={`w-14 h-14 rounded-full border-3 transition-all ${
                    currentCharacter.skinTone === tone.value
                      ? 'border-primary shadow-lg ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: tone.color }}
                  aria-label={tone.value}
                />
              ))}
            </div>
          </div>

          {/* Hair Color */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Hair Color</h3>
            <div className="flex gap-3 flex-wrap">
              {HAIR_COLORS.map((color) => (
                <motion.button
                  key={color.value}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleAppearanceChange('hairColor', color.value)}
                  className={`w-14 h-14 rounded-full border-3 transition-all ${
                    currentCharacter.hairColor === color.value
                      ? 'border-primary shadow-lg ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color.color }}
                  aria-label={color.value}
                />
              ))}
            </div>
          </div>

          {/* Eye Color */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Eye Color</h3>
            <div className="flex gap-3 flex-wrap">
              {EYE_COLORS.map((color) => (
                <motion.button
                  key={color.value}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleAppearanceChange('eyeColor', color.value)}
                  className={`w-14 h-14 rounded-full border-3 transition-all relative overflow-hidden ${
                    currentCharacter.eyeColor === color.value
                      ? 'border-primary shadow-lg ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  aria-label={color.value}
                >
                  <div className="absolute inset-2 rounded-full" style={{ backgroundColor: color.color }} />
                  <div className="absolute inset-0 rounded-full border-4 border-white/20" />
                </motion.button>
              ))}
            </div>
          </div>

          {/* Hair Texture */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Hair Texture</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {HAIR_TEXTURES.map((texture) => (
                <motion.button
                  key={texture.value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleAppearanceChange('hairTexture', texture.value)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    currentCharacter.hairTexture === texture.value
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl mb-1">{texture.icon}</div>
                  <div className="text-sm font-medium">{texture.label}</div>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation hint */}
      <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
        <span>Customizing {charactersData[activeTab]?.name || `Person ${activeTab + 1}`}</span>
        {activeTab < characterCount - 1 && (
          <>
            <span>•</span>
            <button
              onClick={() => setActiveTab(activeTab + 1)}
              className="text-primary hover:underline"
            >
              Next character →
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
}
